import { z } from 'zod';
import { CreditApplication, PricingAggregates } from '~/contracts/flp';
import { logError } from '~/utilities/loggers';
import { CreditApplicationSchema } from './creditApplicationSchema';
import { PricingAggregatesSchema } from './pricingAggregatesSchema';
import showApiError from './showApiError';

/**
 * Validates data against a Zod schema and logs errors
 */
function validateAndLog<T>(
  data: unknown,
  schema: z.ZodType<T>,
  dataType: string
): boolean {
  const result = schema.safeParse(data);

  if (!result.success) {
    const validationErrors = result.error.errors.map(error => ({
      path: error.path.join('.'),
      message: error.message,
      code: error.code
    }));

    logError(
      new Error(
        `[FLP Validation Error] ${dataType} received data with validation errors`
      ),
      { location: `FLP Validation - ${dataType}`, validationErrors }
    );
  }

  return result.success;
}

/**
 * Validates creditApplication and pricingAggregates data before processing
 * in the FinancingContext
 */
export default function validateFinancingData(
  creditApplication: CreditApplication,
  pricingAggregates?: PricingAggregates
): void {
  const creditApplicationIsValid = validateAndLog(
    creditApplication,
    CreditApplicationSchema,
    'creditApplication'
  );
  let pricingAggregatesIsValid = true;

  if (pricingAggregates) {
    pricingAggregatesIsValid = validateAndLog(
      pricingAggregates,
      PricingAggregatesSchema,
      'pricingAggregates'
    );
  }

  if (!creditApplicationIsValid || !pricingAggregatesIsValid) {
    showApiError();
  }
}
